import { forwardRef, type Ref, type ReactNode, type InputHTMLAttributes, type ChangeEvent } from 'react';
import { useUncontrolled } from '@repletoryreach/sdk-utils/hooks';
import { ActionIcon } from '../../core/ActionIcon/ActionIcon.js';
import { InputWrapper, type InputWrapperBaseProps } from '../shared/InputBase.js';

export interface NumberInputProps
  extends InputWrapperBaseProps,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'color' | 'type' | 'value' | 'onChange' | 'defaultValue'> {
  value?: number | '';
  defaultValue?: number | '';
  onChange?: (value: number | '') => void;
  min?: number;
  max?: number;
  step?: number;
  hideControls?: boolean;
}

export const NumberInput = forwardRef(function NumberInput(
  props: NumberInputProps,
  ref: Ref<HTMLInputElement>
): ReactNode {
  const {
    label, description, error, required, withAsterisk,
    leftSection, rightSection, leftSectionWidth, rightSectionWidth,
    size, className, style, id,
    value, defaultValue, onChange,
    min, max, step = 1, hideControls,
    ...inputProps
  } = props;

  const [val, setVal] = useUncontrolled<number | ''>({
    value,
    defaultValue,
    finalValue: '',
    onChange
  });

  const apply = (next: number | '') => {
    let n = next;
    if (typeof n === 'number') {
      if (min !== undefined) n = Math.max(min, n);
      if (max !== undefined) n = Math.min(max, n);
    }
    setVal(n);
  };

  const inc = () => apply(typeof val === 'number' ? val + step : (min ?? 0));
  const dec = () => apply(typeof val === 'number' ? val - step : (min ?? 0));

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.currentTarget.value;
    if (raw === '') return apply('');
    const n = Number(raw);
    if (Number.isFinite(n)) apply(n);
  };

  const stepperSection = hideControls ? rightSection : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, pointerEvents: 'auto' }}>
      <ActionIcon size={16} variant="subtle" aria-label="Increment" type="button" onClick={inc}>
        <svg width="9" height="6" viewBox="0 0 9 6" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="1,5 4.5,1 8,5" /></svg>
      </ActionIcon>
      <ActionIcon size={16} variant="subtle" aria-label="Decrement" type="button" onClick={dec}>
        <svg width="9" height="6" viewBox="0 0 9 6" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="1,1 4.5,5 8,1" /></svg>
      </ActionIcon>
    </div>
  );

  return (
    <InputWrapper
      label={label}
      description={description}
      error={error}
      required={required}
      withAsterisk={withAsterisk}
      leftSection={leftSection}
      rightSection={stepperSection}
      leftSectionWidth={leftSectionWidth}
      rightSectionWidth={rightSectionWidth ?? 28}
      size={size}
      className={className}
      style={style}
      inputId={id}
    >
      {({ inputId, describedBy, invalid, className: ctlClass }) => (
        <input
          ref={ref}
          id={inputId}
          type="number"
          inputMode="numeric"
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={ctlClass}
          value={val}
          onChange={handleChange}
          min={min}
          max={max}
          step={step}
          {...inputProps}
        />
      )}
    </InputWrapper>
  );
});
